import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, User, Calendar, Clock, MapPin, DollarSign, Phone, Mail,
  AlertTriangle, CheckCircle, Package, FileText, MessageSquare,
  Plus, Edit2, Trash2, Save, Loader2, ChevronDown, ChevronUp,
  Users, UserPlus, UserMinus, Minus
} from 'lucide-react';
import { Button } from '../ui/button';
import api, { notesAPI } from '../../services/api';
import { toast } from 'sonner';

const BookingDetailModal = ({ isOpen, onClose, booking, onRefresh, availableWorkers = [] }) => {
  const [activeSection, setActiveSection] = useState('details');
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [notesExpanded, setNotesExpanded] = useState(true);
  
  // Worker assignment state
  const [workers, setWorkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [workersNeeded, setWorkersNeeded] = useState(1);
  const [savingWorkersNeeded, setSavingWorkersNeeded] = useState(false);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [assigningWorkers, setAssigningWorkers] = useState(false);

  useEffect(() => {
    if (isOpen && booking?.id) {
      fetchNotes();
      fetchWorkers();
      setWorkersNeeded(booking.workers_needed || 1);
      // Pre-select already assigned workers
      const assignedIds = booking.worker_ids || (booking.worker_id ? [booking.worker_id] : []);
      setSelectedWorkerIds(assignedIds);
    }
  }, [isOpen, booking?.id]);

  const fetchWorkers = async () => {
    try {
      setLoadingWorkers(true);
      const response = await api.get('/workers');
      setWorkers(response.data || []);
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoadingWorkers(false);
    }
  };

  const fetchNotes = async () => {
    try {
      setLoadingNotes(true);
      const response = await notesAPI.getNotes('booking', booking.id);
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      setSavingNote(true);
      const response = await notesAPI.create({
        entity_type: 'booking',
        entity_id: booking.id,
        content: newNote.trim()
      });
      setNotes([response.data, ...notes]);
      setNewNote('');
      toast.success('Note added');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editContent.trim()) return;
    try {
      setSavingNote(true);
      await notesAPI.update(noteId, editContent.trim());
      setNotes(notes.map(n =>
        n.id === noteId ? { ...n, content: editContent.trim(), updated_at: new Date().toISOString() } : n
      ));
      setEditingId(null);
      setEditContent('');
      toast.success('Note updated');
    } catch (error) {
      toast.error('Failed to update note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Archive this note?')) return;
    try {
      await notesAPI.delete(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note archived');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to archive note');
    }
  };

  // Handle updating workers needed (large jobs only)
  const handleUpdateWorkersNeeded = async () => {
    if (workersNeeded < 1 || workersNeeded > 10) {
      toast.error('Workers needed must be between 1 and 10');
      return;
    }
    
    try {
      setSavingWorkersNeeded(true);
      await api.put(`/bookings/${booking.id}/workers-needed?workers_needed=${workersNeeded}`);
      toast.success(`Updated to ${workersNeeded} worker(s) needed`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workers needed');
    } finally {
      setSavingWorkersNeeded(false);
    }
  };

  // Handle assigning a single worker
  const handleAssignWorker = async (workerId) => {
    try {
      setAssigningWorkers(true);
      await api.post(`/bookings/${booking.id}/assign?worker_id=${workerId}`);
      toast.success('Worker assigned successfully');
      // Add to selected list
      setSelectedWorkerIds(prev => [...prev, workerId]);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign worker');
    } finally {
      setAssigningWorkers(false);
    }
  };

  // Handle assigning multiple workers at once
  const handleAssignMultipleWorkers = async () => {
    if (selectedWorkerIds.length === 0) {
      toast.error('Please select at least one worker');
      return;
    }
    
    try {
      setAssigningWorkers(true);
      await api.post(`/bookings/${booking.id}/assign-multiple`, selectedWorkerIds);
      toast.success(`${selectedWorkerIds.length} worker(s) assigned successfully`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign workers');
    } finally {
      setAssigningWorkers(false);
    }
  };

  // Handle removing a worker from the booking
  const handleRemoveWorker = async (workerId) => {
    if (!window.confirm('Remove this worker from the booking?')) return;
    
    try {
      setAssigningWorkers(true);
      await api.delete(`/bookings/${booking.id}/workers/${workerId}`);
      toast.success('Worker removed from booking');
      setSelectedWorkerIds(prev => prev.filter(id => id !== workerId));
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove worker');
    } finally {
      setAssigningWorkers(false);
    }
  };

  // Toggle worker selection
  const toggleWorkerSelection = (workerId) => {
    setSelectedWorkerIds(prev => 
      prev.includes(workerId) 
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  // Check if job needs multiple workers
  const isMultiWorkerJob = booking?.service_type === 'errands' || booking?.service_name?.toLowerCase().includes('large');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
      confirmed: 'bg-[#D4AF37]/10 text-[#D4AF37] border-blue-300',
      in_progress: 'bg-[#F4C2C2]/15 text-purple-700 border-purple-300',
      completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      cancelled: 'bg-[var(--error)]/15 text-red-700 border-red-300'
    };
    return styles[status] || 'bg-[var(--background-subtle)] text-[var(--text-primary)] border-[var(--border-primary)]';
  };

  const getPaymentBadge = (status) => {
    return status === 'paid'
      ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
      : 'bg-amber-100 text-amber-700 border-amber-300';
  };

  if (!isOpen || !booking) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[var(--background-paper)] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="booking-detail-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-xl font-bold font-['Cormorant_Garamond']">{booking.service_name}</h2>
                  {booking.is_urgent && (
                    <span className="bg-amber-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      URGENT
                    </span>
                  )}
                </div>
                <p className="text-white/80 text-sm">
                  Booking ID: {booking.id?.substring(0, 8)}...
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-[var(--background-paper)]/20 rounded-lg transition-colors"
                data-testid="close-booking-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Status Badges */}
            <div className="flex items-center gap-3 mt-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadge(booking.status)}`}>
                {booking.status?.replace('_', ' ').toUpperCase()}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPaymentBadge(booking.payment_status)}`}>
                {booking.payment_status?.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b bg-[var(--background-subtle)]">
            {[
              { id: 'details', label: 'Booking Details', icon: FileText },
              { id: 'workers', label: 'Workers', icon: Users, badge: booking.workers?.length || (booking.worker_id ? 1 : 0) },
              { id: 'notes', label: 'Notes', icon: MessageSquare, badge: notes.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                  activeSection === tab.id
                    ? 'bg-[var(--background-paper)] text-[var(--primary)] border-b-2 border-[var(--primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--background-subtle)]'
                }`}
                data-testid={`modal-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="bg-[#E6C965] text-white text-xs px-2 py-0.5 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeSection === 'details' && (
              <div className="space-y-6">
                {/* Customer Info */}
                <div className="bg-[var(--background-subtle)] rounded-xl p-4">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-[var(--primary)]" />
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Name</p>
                      <p className="font-medium">{booking.customer_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Email</p>
                      <p className="font-medium flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {booking.customer_email || 'N/A'}
                      </p>
                    </div>
                    {booking.client_phone && (
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Phone</p>
                        <p className="font-medium flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {booking.client_phone}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Schedule & Location */}
                <div className="bg-[var(--background-subtle)] rounded-xl p-4">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[var(--primary)]" />
                    Schedule & Location
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Date</p>
                      <p className="font-medium">{booking.scheduled_date}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Time</p>
                      <p className="font-medium flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {booking.scheduled_time}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Duration</p>
                      <p className="font-medium">{booking.duration_hours} hour(s)</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs text-[var(--text-muted)] uppercase">Address</p>
                      <p className="font-medium flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {booking.address}, {booking.city}, {booking.state} {booking.zip_code}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Worker Assignment */}
                {booking.worker_name && (
                  <div className="bg-[#D4AF37]/10 rounded-xl p-4">
                    <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-[#D4AF37]" />
                      Assigned Worker
                    </h3>
                    <p className="font-medium text-[#D4AF37]">{booking.worker_name}</p>
                  </div>
                )}

                {/* Payment & Pricing */}
                <div className="bg-emerald-50 rounded-xl p-4">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    Payment Details
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Base Amount</p>
                      <p className="font-medium">${booking.base_amount?.toFixed(2) || '0.00'}</p>
                    </div>
                    {booking.is_urgent && (
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Urgency Fee</p>
                        <p className="font-medium text-amber-600">+25%</p>
                      </div>
                    )}
                    {(booking.distance_miles > 0 || booking.mileage_fee > 0) && (
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Distance</p>
                        <p className="font-medium">{booking.distance_miles?.toFixed(1) || 0} miles</p>
                      </div>
                    )}
                    {booking.mileage_fee > 0 && (
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Mileage Fee</p>
                        <p className="font-medium">${booking.mileage_fee?.toFixed(2)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase">Total</p>
                      <p className="font-bold text-lg text-emerald-700">${booking.total_amount?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Selected Add-ons */}
                {booking.selected_addons && booking.selected_addons.length > 0 && (
                  <div className="bg-[#F4C2C2]/10 rounded-xl p-4">
                    <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4 text-[#F4C2C2]" />
                      Selected Add-Ons
                    </h3>
                    <div className="space-y-2">
                      {booking.selected_addons.map((addon) => (
                        <div key={addon.name || addon.id} className="flex justify-between items-center py-2 border-b border-purple-100 last:border-0">
                          <span className="font-medium">{addon.name}</span>
                          <span className="text-purple-700">${addon.price?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Special Instructions */}
                {booking.special_instructions && (
                  <div className="bg-[var(--warning)]/10 rounded-xl p-4">
                    <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-amber-600" />
                      Special Instructions
                    </h3>
                    <p className="text-[var(--text-primary)] whitespace-pre-wrap">{booking.special_instructions}</p>
                  </div>
                )}

                {/* Status Reason */}
                {booking.status_reason && (
                  <div className="bg-[var(--error)]/10 rounded-xl p-4">
                    <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-[var(--error)]" />
                      Status Note
                    </h3>
                    <p className="text-red-700">{booking.status_reason}</p>
                  </div>
                )}
              </div>
            )}

            {activeSection === 'workers' && (
              <div className="space-y-6">
                {/* Workers Needed Section - Only for Large Jobs */}
                {isMultiWorkerJob && (
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                    <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      Workers Needed (Large Job)
                    </h3>
                    <p className="text-sm text-[var(--text-muted)] mb-3">
                      Set how many workers are required for this job.
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-purple-300 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setWorkersNeeded(Math.max(1, workersNeeded - 1))}
                          className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors"
                          disabled={workersNeeded <= 1}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input
                          type="number"
                          value={workersNeeded}
                          onChange={(e) => setWorkersNeeded(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                          className="w-16 text-center py-2 border-0 focus:ring-0 text-lg font-bold text-purple-700"
                          min="1"
                          max="10"
                          data-testid="workers-needed-input"
                        />
                        <button
                          onClick={() => setWorkersNeeded(Math.min(10, workersNeeded + 1))}
                          className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors"
                          disabled={workersNeeded >= 10}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <Button
                        onClick={handleUpdateWorkersNeeded}
                        disabled={savingWorkersNeeded || workersNeeded === (booking.workers_needed || 1)}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="save-workers-needed-btn"
                      >
                        {savingWorkersNeeded ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-1" />
                            Save
                          </>
                        )}
                      </Button>
                    </div>
                    {(booking.workers_needed || 1) !== workersNeeded && (
                      <p className="text-xs text-purple-600 mt-2">
                        Current: {booking.workers_needed || 1} worker(s) • Click Save to update
                      </p>
                    )}
                  </div>
                )}

                {/* Currently Assigned Workers */}
                <div className="bg-[#D4AF37]/10 rounded-xl p-4">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#D4AF37]" />
                    Assigned Workers
                    {isMultiWorkerJob && booking.workers_needed > 1 && (
                      <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full ml-2">
                        {booking.workers?.length || (booking.worker_id ? 1 : 0)}/{booking.workers_needed || 1} assigned
                      </span>
                    )}
                  </h3>
                  
                  {(booking.workers && booking.workers.length > 0) ? (
                    <div className="space-y-2">
                      {booking.workers.map((worker, idx) => (
                        <div key={worker.id || idx} className="flex items-center justify-between bg-[var(--background-subtle)] rounded-lg p-3 border border-[#D4AF37]/30">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-[#D4AF37]" />
                            </div>
                            <div>
                              <p className="font-medium text-[var(--text-primary)]">{worker.name}</p>
                              <p className="text-xs text-[var(--text-muted)]">
                                {worker.role === 'primary' ? 'Lead Worker' : 'Helper'}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveWorker(worker.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            disabled={assigningWorkers}
                          >
                            <UserMinus className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : booking.worker_name ? (
                    <div className="flex items-center justify-between bg-[var(--background-subtle)] rounded-lg p-3 border border-[#D4AF37]/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <div>
                          <p className="font-medium text-[var(--text-primary)]">{booking.worker_name}</p>
                          <p className="text-xs text-[var(--text-muted)]">Assigned Worker</p>
                        </div>
                      </div>
                      {booking.worker_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveWorker(booking.worker_id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          disabled={assigningWorkers}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-[var(--text-muted)]">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No workers assigned yet</p>
                      <p className="text-sm">Select workers below to assign them</p>
                    </div>
                  )}
                </div>

                {/* Assign Workers Section */}
                <div className="bg-[var(--background-subtle)] rounded-xl p-4">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-emerald-600" />
                    Assign Workers
                  </h3>
                  <p className="text-sm text-[var(--text-muted)] mb-3">
                    Select worker(s) to assign to this {booking.service_type || 'job'}.
                  </p>
                  
                  {loadingWorkers ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[var(--primary)]" />
                    </div>
                  ) : workers.length === 0 ? (
                    <div className="text-center py-6 text-[var(--text-muted)]">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No workers available</p>
                      <p className="text-sm">Add workers from the Workers tab</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {workers.map((worker) => {
                          const isAssigned = booking.worker_ids?.includes(worker.id) || 
                                            booking.worker_ids?.includes(worker.user_id) ||
                                            booking.worker_id === worker.id ||
                                            booking.worker_id === worker.user_id;
                          const isSelected = selectedWorkerIds.includes(worker.id) || selectedWorkerIds.includes(worker.user_id);
                          
                          return (
                            <div
                              key={worker.id}
                              onClick={() => !isAssigned && toggleWorkerSelection(worker.user_id || worker.id)}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                                isAssigned 
                                  ? 'bg-[#D4AF37]/10 border-[#D4AF37] cursor-not-allowed opacity-60'
                                  : isSelected
                                    ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-200'
                                    : 'bg-[var(--background-subtle)] border-[var(--border-primary)] hover:border-emerald-500/50 hover:bg-emerald-500/10'
                              }`}
                              data-testid={`worker-option-${worker.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  isAssigned ? 'bg-[#D4AF37]/20' : isSelected ? 'bg-emerald-200' : 'bg-gray-100'
                                }`}>
                                  <User className={`w-5 h-5 ${
                                    isAssigned ? 'text-[#D4AF37]' : isSelected ? 'text-emerald-600' : 'text-gray-500'
                                  }`} />
                                </div>
                                <div>
                                  <p className="font-medium text-[var(--text-primary)]">{worker.full_name}</p>
                                  <p className="text-xs text-[var(--text-muted)]">
                                    {worker.service_types?.join(', ') || 'All services'}
                                  </p>
                                </div>
                              </div>
                              <div>
                                {isAssigned ? (
                                  <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-1 rounded-full">
                                    Assigned
                                  </span>
                                ) : isSelected ? (
                                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                                ) : (
                                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Assign Button */}
                      {selectedWorkerIds.some(id => !booking.worker_ids?.includes(id) && booking.worker_id !== id) && (
                        <Button
                          onClick={handleAssignMultipleWorkers}
                          disabled={assigningWorkers}
                          className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="assign-workers-btn"
                        >
                          {assigningWorkers ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <UserPlus className="w-4 h-4 mr-2" />
                          )}
                          Assign {selectedWorkerIds.filter(id => !booking.worker_ids?.includes(id) && booking.worker_id !== id).length} Worker(s)
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'notes' && (
              <div className="space-y-4">
                {/* Add Note Section */}
                <div className="bg-[#D4AF37]/10 rounded-xl p-4">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                    <Plus className="w-4 h-4 text-[#D4AF37]" />
                    Add New Note
                  </h3>
                  <div className="flex gap-3">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Type your note here..."
                      className="flex-1 px-4 py-3 border border-[var(--border-primary)] rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-[#D4AF37] resize-none"
                      rows={3}
                      disabled={savingNote}
                      data-testid="new-note-textarea"
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={!newNote.trim() || savingNote}
                      className="self-end bg-[#E6C965] hover:bg-blue-700 text-white px-6"
                      data-testid="add-note-btn"
                    >
                      {savingNote ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Notes List */}
                <div className="bg-[var(--background-subtle)] rounded-xl p-4">
                  <button
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    className="w-full flex items-center justify-between mb-3"
                  >
                    <h3 className="font-semibold text-[var(--text-primary)] flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-[var(--text-secondary)]" />
                      All Notes ({notes.length})
                    </h3>
                    {notesExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {notesExpanded && (
                    <div className="space-y-3">
                      {loadingNotes ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
                        </div>
                      ) : notes.length === 0 ? (
                        <div className="text-center py-8 text-[var(--text-muted)]">
                          <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                          <p>No notes yet</p>
                          <p className="text-sm">Add your first note above</p>
                        </div>
                      ) : (
                        notes.map((note) => (
                          <div
                            key={note.id}
                            className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                            data-testid={`note-${note.id}`}
                          >
                            {editingId === note.id ? (
                              <div className="space-y-3">
                                <textarea
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  className="w-full px-3 py-2 border border-[var(--border-primary)] rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                                  rows={3}
                                  autoFocus
                                />
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingId(null);
                                      setEditContent('');
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateNote(note.id)}
                                    disabled={!editContent.trim() || savingNote}
                                    className="bg-[#E6C965] hover:bg-blue-700 text-white"
                                  >
                                    <Save className="w-3 h-3 mr-1" />
                                    Save
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <p className="text-[var(--text-primary)] whitespace-pre-wrap">{note.content}</p>
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                  <div className="text-xs text-[var(--text-muted)]">
                                    <span className="font-medium">{note.created_by_name || 'Owner'}</span>
                                    <span className="mx-1">•</span>
                                    <span>{formatDate(note.created_at)}</span>
                                    {note.updated_at && (
                                      <span className="text-[#D4AF37] ml-1">(edited)</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => {
                                        setEditingId(note.id);
                                        setEditContent(note.content);
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors"
                                      title="Edit note"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteNote(note.id)}
                                      className="p-1.5 text-gray-400 hover:text-[var(--error)] hover:bg-[var(--error)]/10 rounded-lg transition-colors"
                                      title="Archive note"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BookingDetailModal;
